import {
  emailError,
  isValidPhone,
  isValidZip,
  phoneDigits,
  phoneError,
} from 'features/scla/utils/contactFields';
import { isSclaReturnerAppType } from 'utils/sclaApplicationKind';
import type { SclaApplication } from '../types';
import { countWords, PROMPT_WORD_LIMIT } from './promptFields';

export type SclaStepId =
  | 'background'
  | 'family'
  | 'school'
  | 'prompts'
  | 'transcripts'
  | 'rec'
  | 'signature';

export type SclaStepState = 'complete' | 'started' | 'empty' | 'optional';

export interface ISclaMissingField {
  fieldId: string;
  label: string;
}

export const SCLA_REQUIRED_STEP_IDS: SclaStepId[] = [
  'background',
  'family',
  'school',
  'prompts',
  'transcripts',
  'signature',
];

function text(value: string | null | undefined): string {
  return (value || '').trim();
}

function filled(value: string | null | undefined): boolean {
  return text(value) !== '';
}

function dobIssue(value: Date | string | null | undefined): string {
  if (!value) return 'date of birth';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'date of birth';
  const today = new Date();
  const born = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (born.getTime() > todayStart.getTime()) {
    return 'date of birth (cannot be in the future)';
  }
  let age = todayStart.getFullYear() - born.getFullYear();
  const month = todayStart.getMonth() - born.getMonth();
  if (month < 0 || (month === 0 && todayStart.getDate() < born.getDate())) age -= 1;
  if (age < 10 || age > 20) return 'date of birth (age must be between 10 and 20)';
  return '';
}

function fileFilled(file: { url?: string; _id?: string } | null | undefined): boolean {
  return Boolean(file && (file.url || file._id));
}

function backgroundStarted(app: SclaApplication): boolean {
  const info = app.backgroundInfo;
  return Boolean(
    filled(info.firstName)
    || filled(info.lastName)
    || filled(info.email)
    || filled(info.studentPhoneNumber)
    || filled(info.parentPhoneNumber)
    || filled(info.streetAddress)
    || filled(info.cityAddress)
    || filled(info.stateAddress)
    || filled(info.zipAddress)
    || info.dob
    || filled(info.instagramHandle)
    || filled(info.ethnicity)
    || filled(info.gradeLevel)
    || filled(info.primaryLanguage)
    || filled(info.hearAboutUs)
    || filled(info.appliedBefore)
    || filled(info.transportation),
  );
}

function familyStarted(app: SclaApplication): boolean {
  const info = app.familyInfo;
  return Boolean(
    filled(info.parentsMaritalStatus)
    || filled(info.numOfBrothers)
    || filled(info.numOfSisters)
    || filled(info.motherName)
    || filled(info.fatherName)
    || filled(info.guardianName)
    || info.motherIncluded === true
    || info.fatherIncluded === true
    || info.guardianIncluded === true
    || filled(info.motherStatus)
    || filled(info.fatherStatus),
  );
}

function schoolStarted(app: SclaApplication): boolean {
  const info = app.schoolInfo;
  return Boolean(
    filled(info.schoolName)
    || filled(info.schoolOther)
    || filled(info.counselorName)
    || filled(info.counselorEmail)
    || filled(info.counselorPhoneNumber)
    || filled(info.gpa)
    || filled(info.hobbies)
    || (info.careerInterests || []).length
    || (info.extraCurriculars || []).some(item =>
      filled(item.name) || filled(item.position) || filled(item.hours) || filled(item.grade),
    ),
  );
}

export function sclaPromptsComplete(app: SclaApplication): boolean {
  const info = app.promptInfo;
  if ((info.wordSelection || []).length !== 3) return false;
  const answers = isSclaReturnerAppType(app.appType)
    ? [info.returnerSkillPrompt, info.returnerCuriosityPrompt, info.returnerCreedPrompt]
    : [info.obstaclePrompt, info.curiosityPrompt];
  return answers.every(value => {
    const count = countWords(value);
    return count > 0 && count <= PROMPT_WORD_LIMIT;
  });
}

function promptsStarted(app: SclaApplication): boolean {
  const info = app.promptInfo;
  return Boolean(
    (info.wordSelection || []).length
    || filled(info.obstaclePrompt)
    || filled(info.curiosityPrompt)
    || filled(info.returnerSkillPrompt)
    || filled(info.returnerCuriosityPrompt)
    || filled(info.returnerCreedPrompt),
  );
}

function signatureStarted(app: SclaApplication): boolean {
  const info = app.signatureInfo;
  return fileFilled(info.studentSignature) || fileFilled(info.parentSignature);
}

export function sclaStepState(app: SclaApplication, id: SclaStepId): SclaStepState {
  if (id === 'rec' && !(app.recInfo.files || []).length && !app.recInfo.sectionComplete) {
    return 'optional';
  }
  if (id === 'prompts') {
    if (sclaPromptsComplete(app)) return 'complete';
    if (promptsStarted(app)) return 'started';
    return 'empty';
  }
  const complete = {
    background: app.backgroundInfo.sectionComplete,
    family: app.familyInfo.sectionComplete,
    school: app.schoolInfo.sectionComplete,
    prompts: app.promptInfo.sectionComplete,
    transcripts: app.transcriptsInfo.sectionComplete,
    rec: app.recInfo.sectionComplete,
    signature: app.signatureInfo.sectionComplete,
  }[id];
  if (complete) return 'complete';
  const started = {
    background: backgroundStarted(app),
    family: familyStarted(app),
    school: schoolStarted(app),
    prompts: promptsStarted(app),
    transcripts: (app.transcriptsInfo.files || []).length > 0,
    rec: (app.recInfo.files || []).length > 0,
    signature: signatureStarted(app),
  }[id];
  return started ? 'started' : 'empty';
}

export function sclaRequiredProgress(app: SclaApplication): { done: number; total: number } {
  const done = SCLA_REQUIRED_STEP_IDS.filter(id => sclaStepState(app, id) === 'complete').length;
  return { done, total: SCLA_REQUIRED_STEP_IDS.length };
}

function push(items: ISclaMissingField[], fieldId: string, label: string, show: boolean) {
  if (show) items.push({ fieldId, label });
}

function backgroundMissing(app: SclaApplication): ISclaMissingField[] {
  const info = app.backgroundInfo;
  const items: ISclaMissingField[] = [];
  push(items, 'scla-first', 'first name', !filled(info.firstName));
  push(items, 'scla-last', 'last name', !filled(info.lastName));
  const born = dobIssue(info.dob);
  push(items, 'scla-dob', born, Boolean(born));
  push(items, 'scla-language', 'primary language', !filled(info.primaryLanguage));
  push(items, 'scla-grade', 'grade level', !filled(info.gradeLevel));
  push(items, 'scla-ethnicity', 'ethnicity', !filled(info.ethnicity));
  const email = text(info.email);
  push(items, 'scla-email', email && emailError(email, false) ? 'email (enter a valid email)' : 'email', !email || Boolean(emailError(email, false)));
  const parentPhone = text(info.parentPhoneNumber);
  const parentPhoneBad = parentPhone && !isValidPhone(parentPhone);
  push(
    items,
    'scla-parent-phone',
    parentPhoneBad ? 'parent phone (enter a 10-digit number)' : 'parent phone',
    !phoneDigits(parentPhone) || Boolean(parentPhoneBad),
  );
  push(items, 'scla-street', 'street address', !filled(info.streetAddress));
  push(items, 'scla-city', 'city', !filled(info.cityAddress));
  push(items, 'scla-state', 'state', !filled(info.stateAddress));
  const zip = text(info.zipAddress);
  push(items, 'scla-zip', zip && !isValidZip(zip) ? 'zip code (enter 5 digits)' : 'zip code', !isValidZip(zip));
  push(items, 'scla-applied', 'applied before', !filled(info.appliedBefore));
  push(items, 'scla-hear', 'how you heard about SCLA', !filled(info.hearAboutUs));
  push(items, 'scla-transport', 'how you get around', !filled(info.transportation));
  return items;
}

function familyMissing(app: SclaApplication): ISclaMissingField[] {
  const info = app.familyInfo;
  const items: ISclaMissingField[] = [];
  push(items, 'scla-brothers', 'brothers', !filled(info.numOfBrothers));
  push(items, 'scla-sisters', 'sisters', !filled(info.numOfSisters));
  push(items, 'scla-marital', 'parents marital status', !filled(info.parentsMaritalStatus));
  const flags = [info.motherIncluded, info.fatherIncluded, info.guardianIncluded];
  const usesSwitches = flags.some(flag => flag === true || flag === false);
  if (!usesSwitches) return items;
  const motherOn = info.motherIncluded === true;
  const fatherOn = info.fatherIncluded === true;
  const guardianOn = info.guardianIncluded === true;
  if (!motherOn && !fatherOn && !guardianOn) {
    push(items, 'scla-parents', 'at least one parent or guardian', true);
  }
  push(items, 'mother-name', "Mother's name", motherOn && !filled(info.motherName));
  push(items, 'father-name', "Father's name", fatherOn && !filled(info.fatherName));
  push(
    items,
    'guardian-rel',
    "the guardian's relationship",
    guardianOn && !filled(info.guardianRelationship),
  );
  push(items, 'guardian-name', "the guardian's name", guardianOn && !filled(info.guardianName));
  return items;
}

function gpaMissing(value: string | null | undefined): boolean {
  const gpa = text(value);
  if (!/^\d+(\.\d{1,2})?$/.test(gpa)) return true;
  const number = Number(gpa);
  return number < 0 || number > 5;
}

function schoolMissing(app: SclaApplication): ISclaMissingField[] {
  const info = app.schoolInfo;
  const items: ISclaMissingField[] = [];
  const schoolId = text(info.schoolName).toLowerCase() === 'other' ? 'school-other' : 'school-name';
  const email = text(info.counselorEmail);
  const phone = text(info.counselorPhoneNumber);
  const emailBad = Boolean(email) && Boolean(emailError(email, false));
  const phoneBad = Boolean(phone) && Boolean(phoneError(phone, false));
  const interests = (info.careerInterests || []).map(item => text(item)).filter(Boolean);
  const otherSelected = interests.some(item => item.toLowerCase() === 'other');
  push(items, schoolId, 'school', !filled(info.schoolName));
  push(items, 'school-gpa', 'GPA (0.0 to 5.0)', gpaMissing(info.gpa));
  push(items, 'counselor-name', 'counselor or teacher name', !filled(info.counselorName));
  push(items, 'counselor-email', 'counselor email', emailBad);
  push(items, 'counselor-phone', 'counselor phone', phoneBad);
  push(items, 'counselor-email', 'an email or a phone', !email && !phone);
  push(items, 'scla-careers', 'a career field', interests.length === 0);
  push(items, 'career-other', 'the other career field', otherSelected && !filled(info.careerInterestsOther));
  push(items, 'hobbies', 'hobbies and interests', !filled(info.hobbies));
  return items;
}

function promptGap(value: string | null | undefined, name: string): string {
  const count = countWords(value);
  if (!count) return name;
  if (count > PROMPT_WORD_LIMIT) return `${name} (${PROMPT_WORD_LIMIT} words or fewer)`;
  return '';
}

function promptsMissing(app: SclaApplication): ISclaMissingField[] {
  const info = app.promptInfo;
  const items: ISclaMissingField[] = [];
  push(items, 'scla-words', 'three words', (info.wordSelection || []).length !== 3);
  const fields: Array<[string, string | null | undefined, string]> = isSclaReturnerAppType(app.appType)
    ? [
      ['returnerSkillPrompt', info.returnerSkillPrompt, 'the first prompt'],
      ['returnerCuriosityPrompt', info.returnerCuriosityPrompt, 'the second prompt'],
      ['returnerCreedPrompt', info.returnerCreedPrompt, 'the third prompt'],
    ]
    : [
      ['obstaclePrompt', info.obstaclePrompt, 'the first prompt'],
      ['curiosityPrompt', info.curiosityPrompt, 'the second prompt'],
    ];
  fields.forEach(([fieldId, value, name]) => {
    const label = promptGap(value, name);
    push(items, fieldId, label, Boolean(label));
  });
  return items;
}

function signatureMissing(app: SclaApplication): ISclaMissingField[] {
  const info = app.signatureInfo;
  const items: ISclaMissingField[] = [];
  push(items, 'student-signer', 'student name', !filled(info.studentFullName));
  push(items, 'student-date', 'student date', !filled(info.studentSignedDate));
  push(items, 'student-signature', 'student signature', !fileFilled(info.studentSignature));
  push(items, 'parent-signer', 'parent name', !filled(info.parentFullName));
  push(items, 'parent-date', 'parent date', !filled(info.parentSignedDate));
  push(items, 'parent-signature', 'parent signature', !fileFilled(info.parentSignature));
  push(items, 'parent-is-adult', 'parent is 18 or older', info.parentIsAdult !== true);
  push(items, 'information-confirmed', 'confirmation', !app.informationConfirmed);
  return items;
}

export function sclaMissingFields(app: SclaApplication, id: SclaStepId): ISclaMissingField[] {
  if (sclaStepState(app, id) !== 'started') return [];
  if (id === 'background') return backgroundMissing(app);
  if (id === 'family') return familyMissing(app);
  if (id === 'school') return schoolMissing(app);
  if (id === 'prompts') return promptsMissing(app);
  if (id === 'transcripts') return [{ fieldId: 'scla-transcripts', label: 'a transcript file' }];
  if (id === 'rec') return [{ fieldId: 'scla-rec', label: 'a letter of recommendation' }];
  return signatureMissing(app);
}
